import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dataApi } from '@/lib/api';
import { Search, Download, ChevronLeft, ChevronRight } from 'lucide-react';

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000) return (n / 1000).toFixed(1) + 'k';
  return n.toLocaleString();
}

export default function PriorityAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [region, setRegion] = useState('');
  const [headcount, setHeadcount] = useState('');
  const [loading, setLoading] = useState(true);
  const [verticals, setVerticals] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    dataApi.getVerticals().then(r => setVerticals(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      dataApi.getPriorityAccounts({ industry: industry || undefined, region: region || undefined, search: search || undefined, page, headcount: headcount || undefined })
        .then(r => {
          setAccounts(r.data.accounts);
          setTotal(r.data.total);
          setPages(r.data.pages);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [industry, region, search, page, headcount]);

  const exportCsv = () => {
    const header = 'Company,Signals,Asset Views,Departments,Senior Roles,Location,Last Seen\n';
    const rows = accounts.map(a => `"${a.company}",${a.total_signals},${a.asset_views},${a.departments_engaged},${a.senior_roles},"${a.primary_location}","${a.last_seen}"`).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'priority_accounts.csv';
    a.click();
  };

  return (
    <div className="space-y-6" data-testid="priority-accounts-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Priority Accounts</h1>
        <button data-testid="export-csv-btn" onClick={exportCsv} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-600 hover:bg-neutral-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-300" />
          <input
            data-testid="account-search"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search companies..."
            className="w-full h-9 pl-10 pr-3 rounded-lg border border-neutral-200 bg-white text-sm focus:ring-2 focus:ring-[#F66922] outline-none"
          />
        </div>
        <select data-testid="industry-filter" value={industry} onChange={e => { setIndustry(e.target.value); setPage(1); }}
          className="h-9 px-3 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600">
          <option value="">All Industries</option>
          {verticals.map(v => <option key={v.name} value={v.name}>{v.name}</option>)}
        </select>
        <select data-testid="region-filter" value={region} onChange={e => { setRegion(e.target.value); setPage(1); }}
          className="h-9 px-3 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600">
          <option value="">All Regions</option>
          <option value="NA">NA</option>
          <option value="EMEA">EMEA</option>
          <option value="APAC">APAC</option>
          <option value="LATAM">LATAM</option>
        </select>
        <select data-testid="headcount-filter" value={headcount} onChange={e => { setHeadcount(e.target.value); setPage(1); }}
          className="h-9 px-3 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600">
          <option value="">All Headcounts</option>
          <option value="10001+ employees">10001+</option>
          <option value="5001-10000 employees">5001-10000</option>
          <option value="1001-5000 employees">1001-5000</option>
          <option value="501-1000 employees">501-1000</option>
          <option value="201-500 employees">201-500</option>
        </select>
      </div>

      {/* Table */}
      <div className="oaa-card overflow-hidden">
        {loading ? (
          <div className="h-96 skeleton-shimmer" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>Score</th>
                    <th>Company Name</th>
                    <th className="text-right">Signals</th>
                    <th className="text-right">Asset Views</th>
                    <th>Departments</th>
                    <th>Senior Roles</th>
                    <th>Location</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a, i) => {
                    const tierColor = a.tier === 'Hot' ? 'bg-[#F66922] text-white' : a.tier === 'Warm' ? 'bg-orange-100 text-orange-700' : a.tier === 'Cool' ? 'bg-neutral-100 text-neutral-600' : 'bg-neutral-50 text-neutral-400';
                    return (
                    <tr
                      key={i}
                      data-testid={`account-row-${i}`}
                      className="cursor-pointer"
                      onClick={() => navigate(`/account/${encodeURIComponent(a.company)}`)}
                    >
                      <td>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${tierColor}`}>{a.score}</span>
                        </div>
                      </td>
                      <td className="font-medium text-[#0A0A0A] hover:text-[#F66922]">{a.company}</td>
                      <td className="text-right tabular-nums font-medium">{formatNum(a.total_signals)}</td>
                      <td className="text-right tabular-nums">{a.asset_views}</td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-[#F66922] rounded-full" style={{ width: `${Math.min(a.departments_engaged * 15, 100)}%` }} />
                          </div>
                          <span className="text-xs tabular-nums">{a.departments_engaged}</span>
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(a.senior_roles / 10, 100)}%` }} />
                          </div>
                          <span className="text-xs tabular-nums">{a.senior_roles}</span>
                        </div>
                      </td>
                      <td className="text-neutral-500">{a.primary_location}</td>
                      <td className="text-neutral-400 text-xs">{a.last_seen?.split(' ')[0]}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100">
              <span className="text-xs text-neutral-400">{total.toLocaleString()} accounts total</span>
              <div className="flex items-center gap-2">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)}
                  className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-neutral-500">Page {page} of {pages}</span>
                <button disabled={page >= pages} onClick={() => setPage(page + 1)}
                  className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
